// ============================================================
// api.js — Service centralisé pour tous les appels backend
// ============================================================

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000'

// ── Récupère le token JWT stocké après connexion ──
const getToken = () => localStorage.getItem('token')

// ── Requête générique avec gestion du token et des erreurs ──
const request = async (endpoint, options = {}) => {
  const token = getToken()

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  }

  const res = await fetch(`${BASE_URL}${endpoint}`, config)
  const data = await res.json()

  if (!res.ok) {
    throw new Error(data.message || 'Erreur serveur')
  }

  return data
}

// ============================================================
// AUTH
// ============================================================
export const authAPI = {

  // Inscription
  register: (userData) => request('/api/auth/register', {
    method: 'POST',
    body:   JSON.stringify(userData),
  }),

  // Connexion — user ET admin (même endpoint)
  login: (credentials) => request('/api/auth/login', {
    method: 'POST',
    body:   JSON.stringify(credentials),
  }),

  // Profil connecté
  getMe: () => request('/api/auth/me'),

  // Modifier son profil
  updateMe: (data) => request('/api/auth/me', {
    method: 'PUT',
    body:   JSON.stringify(data),
  }),

  // Changer son mot de passe
  changePassword: (data) => request('/api/auth/change-password', {
    method: 'PUT',
    body:   JSON.stringify(data),
  }),

  // Mot de passe oublié
  forgotPassword: (email) => request('/api/auth/forgot-password', {
    method: 'POST',
    body:   JSON.stringify({ email }),
  }),

  // Réinitialiser le mot de passe
  resetPassword: (token, password) => request(`/api/auth/reset-password/${token}`, {
    method: 'POST',
    body:   JSON.stringify({ password }),
  }),

  // Déconnexion — supprime le token local
  logout: () => localStorage.removeItem('token'),
}

// ============================================================
// LISTINGS (ANNONCES)
// ============================================================
export const listingsAPI = {

  // Toutes les annonces actives avec filtres
  getAll: (params = {}) => {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) query.append(k, v)
    })
    return request(`/api/listings?${query.toString()}`)
  },

  // Annonces mises en avant (page d'accueil)
  getFeatured: () => request('/api/listings/featured'),

  // Détail d'une annonce
  getById: (id) => request(`/api/listings/${id}`),

  // Mes propres annonces soumises (utilisateur connecté), tous statuts
  getMy: (params = {}) => {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) query.append(k, v)
    })
    return request(`/api/listings/my?${query.toString()}`)
  },

  // ── Admin uniquement ──

  // Toutes les annonces (admin)
  getAllAdmin: (params = {}) => {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) query.append(k, v)
    })
    return request(`/api/listings/admin/all?${query.toString()}`)
  },

  // Créer une annonce (FormData pour les photos) — tout utilisateur connecté ;
  // une annonce soumise par un non-admin part en attente de validation
  create: (formData) => {
    const token = getToken()
    return fetch(`${BASE_URL}/api/listings`, {
      method:  'POST',
      headers: { Authorization: `Bearer ${token}` },
      body:    formData, // pas de Content-Type car FormData le gère
    }).then(async res => {
      const data = await res.json()
      if (!res.ok) throw new Error(data.message)
      return data
    })
  },

  // Modifier une annonce (admin, ou son auteur pour ses propres annonces)
  update: (id, formData) => {
    const token = getToken()
    return fetch(`${BASE_URL}/api/listings/${id}`, {
      method:  'PUT',
      headers: { Authorization: `Bearer ${token}` },
      body:    formData,
    }).then(async res => {
      const data = await res.json()
      if (!res.ok) throw new Error(data.message)
      return data
    })
  },

  // Supprimer une annonce (admin, ou son auteur pour ses propres annonces)
  delete: (id) => request(`/api/listings/${id}`, { method: 'DELETE' }),

  // Changer le statut — admin uniquement (approuver/rejeter/vendre/remettre en attente)
  updateStatus: (id, status, rejectionReason = '') => request(`/api/listings/${id}/status`, {
    method: 'PATCH',
    body:   JSON.stringify({ status, rejectionReason }),
  }),
}

// ============================================================
// COMMANDES
// ============================================================
export const ordersAPI = {

  // Créer une commande par virement bancaire (paymentStatus: pending_wire)
  create: (listingId, paymentMethod = 'wire') => request('/api/orders', {
    method: 'POST',
    body:   JSON.stringify({ listingId, paymentMethod }),
  }),

  // Mes commandes (user)
  getMy: (params = {}) => {
    const query = new URLSearchParams(params)
    return request(`/api/orders/my?${query.toString()}`)
  },

  // Détail d'une commande
  getById: (id) => request(`/api/orders/${id}`),

  // ── Admin ──

  // Toutes les commandes
  getAllAdmin: (params = {}) => {
    const query = new URLSearchParams(params)
    return request(`/api/orders/admin/all?${query.toString()}`)
  },

  // Statistiques commandes
  getStats: () => request('/api/orders/admin/stats'),

  // Changer le statut de livraison
  updateStatus: (id, deliveryStatus) => request(`/api/orders/${id}/status`, {
    method: 'PATCH',
    body:   JSON.stringify({ deliveryStatus }),
  }),

  // Admin — confirmer la réception d'un virement bancaire
  confirmWire: (id) => request(`/api/orders/${id}/confirm-wire`, { method: 'PATCH' }),

  // Marquer une commande comme lue par l'acheteur
  markAsRead: (id) => request(`/api/orders/${id}/read`, { method: 'PATCH' }),

  // Tout marquer comme lu
  markAllAsRead: () => request('/api/orders/read-all', { method: 'PATCH' }),
}

// ============================================================
// FACTURES
// ============================================================
export const invoicesAPI = {

  // Mes factures (user)
  getMy: (params = {}) => {
    const query = new URLSearchParams(params)
    return request(`/api/invoices/my?${query.toString()}`)
  },

  // Nombre de factures non lues
  getUnreadCount: () => request('/api/invoices/unread/count'),

  // Détail d'une facture
  getById: (id) => request(`/api/invoices/${id}`),

  // Télécharger le PDF — ouvre dans un nouvel onglet
  downloadPDF: (id) => {
    const token = getToken()
    window.open(`${BASE_URL}/api/invoices/${id}/pdf?token=${token}`, '_blank')
  },

  // Marquer une facture comme lue
  markAsRead: (id) => request(`/api/invoices/${id}/read`, { method: 'PATCH' }),

  // Tout marquer comme lu
  markAllAsRead: () => request('/api/invoices/read-all', { method: 'PATCH' }),

  // ── Admin ──
  getAllAdmin: (params = {}) => {
    const query = new URLSearchParams(params)
    return request(`/api/invoices/admin/all?${query.toString()}`)
  },
}

// ============================================================
// MESSAGES
// ============================================================
export const messagesAPI = {

  // Envoyer un message à l'admin (FormData — texte et/ou plusieurs images)
  // Usage: messagesAPI.send({ subject, body, listingId, imageFiles: [File, File...] })
  send: ({ subject = 'Conversation', body = '', listingId = null, imageFiles = [] } = {}) => {
    const formData = new FormData()
    formData.append('subject', subject)
    formData.append('body', body)
    if (listingId) formData.append('listingId', listingId)
    imageFiles.forEach(file => formData.append('images', file))

    const token = getToken()
    return fetch(`${BASE_URL}/api/messages`, {
      method:  'POST',
      headers: { Authorization: `Bearer ${token}` },
      body:    formData, // pas de Content-Type — le navigateur gère le boundary multipart
    }).then(async res => {
      const data = await res.json()
      if (!res.ok) throw new Error(data.message)
      return data
    })
  },

  // Mes messages (user) — liste brute, non utilisée par le nouveau chat (gardée pour compat)
  getMy: (params = {}) => {
    const query = new URLSearchParams(params)
    return request(`/api/messages/my?${query.toString()}`)
  },

  // Mes conversations, groupées par annonce (une par annonce contactée + "general")
  getMyConversations: () => request('/api/messages/my/conversations'),

  // Thread complet pour une annonce donnée ('general' pour les messages sans annonce)
  getMyConversationThread: (key) => request(`/api/messages/my/conversations/${key}`),

  // Nombre de messages non lus
  getUnreadCount: () => request('/api/messages/unread/count'),

  // Détail d'un message + réponses (marque comme lu automatiquement)
  getById: (id) => request(`/api/messages/${id}`),

  // Répondre à un message
  reply: (id, body) => request(`/api/messages/${id}/reply`, {
    method: 'POST',
    body:   JSON.stringify({ body }),
  }),

  // Marquer un message comme lu
  markAsRead: (id) => request(`/api/messages/${id}/read`, { method: 'PATCH' }),

  // Tout marquer comme lu
  markAllAsRead: () => request('/api/messages/read-all', { method: 'PATCH' }),

  // Supprimer un message — 'pour moi' par défaut (masqué uniquement pour moi),
  // ou 'pour tout le monde' (forEveryone: true — réservé à l'expéditeur)
  delete: (id, forEveryone = false) => request(`/api/messages/${id}`, {
    method: 'DELETE',
    body:   JSON.stringify({ forEveryone }),
  }),

  // Supprimer une conversation entière (user) — pour moi uniquement
  deleteMyConversation: (key) => request(`/api/messages/my/conversations/${key}`, {
    method: 'DELETE',
  }),

  // Statut en ligne / hors ligne de l'administrateur (vu par le user)
  getAdminStatus: () => request('/api/messages/status/admin'),

  // ── Admin ──
  getAllAdmin: (params = {}) => {
    const query = new URLSearchParams(params)
    return request(`/api/messages/admin/all?${query.toString()}`)
  },

  // Liste des conversations (une par utilisateur)
  getAdminConversations: () => request('/api/messages/admin/conversations'),

  // Pour un utilisateur donné, ses conversations groupées par annonce (miroir de getMyConversations)
  getAdminUserListingConversations: (userId) => request(`/api/messages/admin/conversations/${userId}/listings`),

  // Thread complet avec un utilisateur pour une annonce précise ('general' sinon)
  getAdminUserListingThread: (userId, key) => request(`/api/messages/admin/conversations/${userId}/listings/${key}`),

  // Envoyer un message à un utilisateur précis (admin) — FormData, texte et/ou plusieurs
  // images, éventuellement rattaché à l'annonce en cours de discussion
  sendAdminMessage: (userId, { body = '', imageFiles = [], listingId = null } = {}) => {
    const formData = new FormData()
    formData.append('body', body)
    if (listingId) formData.append('listingId', listingId)
    imageFiles.forEach(file => formData.append('images', file))

    const token = getToken()
    return fetch(`${BASE_URL}/api/messages/admin/conversations/${userId}`, {
      method:  'POST',
      headers: { Authorization: `Bearer ${token}` },
      body:    formData,
    }).then(async res => {
      const data = await res.json()
      if (!res.ok) throw new Error(data.message)
      return data
    })
  },

  // Supprimer une conversation entière avec un utilisateur (admin) — pour lui uniquement
  deleteAdminConversation: (userId, key) => request(`/api/messages/admin/conversations/${userId}/listings/${key}`, {
    method: 'DELETE',
  }),

  // Statut en ligne / hors ligne d'un utilisateur donné (vu par l'admin)
  getUserStatus: (userId) => request(`/api/messages/status/user/${userId}`),
}

// ============================================================
// FAVORIS
// ============================================================
export const favoritesAPI = {

  // Mes favoris avec pagination
  getMy: (params = {}) => {
    const query = new URLSearchParams(params)
    return request(`/api/favorites/my?${query.toString()}`)
  },

  // IDs de mes annonces favorites
  getMyIds: () => request('/api/favorites/ids'),

  // Ajouter/retirer un favori (toggle)
  toggle: (listingId) => request(`/api/favorites/${listingId}`, {
    method: 'POST',
  }),

  // Supprimer un favori
  remove: (listingId) => request(`/api/favorites/${listingId}`, {
    method: 'DELETE',
  }),

  // Vider tous les favoris
  clearAll: () => request('/api/favorites', { method: 'DELETE' }),
}

// ============================================================
// UTILISATEURS (admin)
// ============================================================
export const usersAPI = {

  // Tous les utilisateurs
  getAll: (params = {}) => {
    const query = new URLSearchParams(params)
    return request(`/api/users?${query.toString()}`)
  },

  // Statistiques
  getStats: () => request('/api/users/stats'),

  // Détail d'un utilisateur
  getById: (id) => request(`/api/users/${id}`),

  // Activer / Bannir
  updateStatus: (id, isActive) => request(`/api/users/${id}/status`, {
    method: 'PATCH',
    body:   JSON.stringify({ isActive }),
  }),

  // Supprimer
  delete: (id) => request(`/api/users/${id}`, { method: 'DELETE' }),
  deleteUser: (id) => request(`/api/users/${id}`, { method: 'DELETE' }),
}

// ── Formulaires d'adoption ────────────────────────────────────────────────
export const adoptionAPI = {

  // Soumettre un formulaire signé (user)
  submit: (data) => request('/api/adoption', {
    method: 'POST',
    body:   JSON.stringify(data),
  }),

  // Mes formulaires soumis (user)
  getMy: () => request('/api/adoption/my'),

  // Détail complet d'un formulaire avec signature (user) — utilisé pour générer le PDF
  getMyFormById: (id) => request(`/api/adoption/my/${id}`),

  // Tous les formulaires (admin) avec pagination
  getAllAdmin: (params = {}) => {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined) query.append(k, v) })
    return request(`/api/adoption/admin/all?${query.toString()}`)
  },

  // Nombre de formulaires non lus (admin)
  getUnreadCountAdmin: () => request('/api/adoption/admin/unread/count'),

  // Détail complet d'un formulaire avec signature (admin) — marque comme lu
  getFormByIdAdmin: (id) => request(`/api/adoption/admin/${id}`),

  // Marquer tout comme lu (admin)
  markAllReadAdmin: () => request('/api/adoption/admin/read-all', { method: 'PATCH' }),
}
// ── Formulaires d'adoption ──────────────────────────────────────────────────
// ── Tracking Transport ──────────────────────────────────────────────────────
export const trackingAPI = {

  // PUBLIC — chercher un trajet par code ANI-XXXX (sans connexion)
  getById: (id) => request(`/api/tracking/trips/${id}`),

  // UTILISATEUR CONNECTÉ — ses propres tickets de transport
  getMy: () => request('/api/tracking/my'),

  // ADMIN — tous les trajets
  getAll: () => request('/api/tracking/trips'),

  // ADMIN — créer / mettre à jour des trajets (avec progression + segments)
  save: (trips, tripsProgress, tripsSegments) => request('/api/tracking/trips', {
    method: 'POST',
    body:   JSON.stringify({ trips, tripsProgress, tripsSegments }),
  }),

  // ADMIN — assigner un ticket à un utilisateur par email
  assign: (tripId, userEmail) => request(`/api/tracking/trips/${tripId}/assign`, {
    method: 'PATCH',
    body:   JSON.stringify({ userEmail }),
  }),

  // ADMIN — mettre à jour la progression d'un trajet
  updateProgress: (tripId, data) => request(`/api/tracking/trips/${tripId}/progress`, {
    method: 'POST',
    body:   JSON.stringify(data),
  }),

  // ADMIN — supprimer un trajet
  delete: (tripId) => request(`/api/tracking/trips/${tripId}`, { method: 'DELETE' }),
}

export const deliveryAPI = {
  // ── Admin — formulaires de livraison ──────────────────────────────────────
  getForms:          ()        => request('/api/delivery/forms'),
  createForm:        (data)    => request('/api/delivery/forms', { method:'POST', body:JSON.stringify(data) }),
  deleteForm:        (id)      => request(`/api/delivery/forms/${id}`, { method:'DELETE' }),
  adminSignForm:     (id, sig) => request(`/api/delivery/forms/${id}/admin-sign`, { method:'PATCH', body:JSON.stringify({ adminSignature:sig }) }),

  // ── Admin — factures de livraison ─────────────────────────────────────────
  getInvoices:       ()        => request('/api/delivery/invoices'),
  createInvoice:     (data)    => request('/api/delivery/invoices', { method:'POST', body:JSON.stringify(data) }),
  deleteInvoice:     (id)      => request(`/api/delivery/invoices/${id}`, { method:'DELETE' }),
  adminSignInvoice:  (id, sig) => request(`/api/delivery/invoices/${id}/admin-sign`, { method:'PATCH', body:JSON.stringify({ adminSignature:sig }) }),

  // ── User — ses propres documents de livraison ──────────────────────────────
  getMyPendingDocs:  ()        => request('/api/delivery/my-pending'),
  getMyPendingCount: ()        => request('/api/delivery/my-pending/count'),
  getMyForm:         (id)      => request(`/api/delivery/forms/${id}/my`),
  getMyInvoice:      (id)      => request(`/api/delivery/invoices/${id}/my`),
  userSignForm:      (id, sig) => request(`/api/delivery/forms/${id}/user-sign`,    { method:'PATCH', body:JSON.stringify({ userSignature:sig }) }),
  userSignInvoice:   (id, sig) => request(`/api/delivery/invoices/${id}/user-sign`, { method:'PATCH', body:JSON.stringify({ userSignature:sig }) }),
}

export const animalDocsAPI = {
  // ── Admin — CRUD ──────────────────────────────────────────────────────────
  getAll:        (docType) => request(`/api/animal-docs${docType ? `?docType=${docType}` : ''}`),
  getOne:        (id)      => request(`/api/animal-docs/${id}`),
  create:        (data)    => request('/api/animal-docs', { method:'POST', body:JSON.stringify(data) }),
  delete:        (id)      => request(`/api/animal-docs/${id}`, { method:'DELETE' }),
  adminSign:     (id, sig) => request(`/api/animal-docs/${id}/admin-sign`, { method:'PATCH', body:JSON.stringify({ adminSignature:sig }) }),

  // ── User — ses propres documents animaliers ────────────────────────────────
  getMyDocs:     ()        => request('/api/animal-docs/my'),
  getMyCount:    ()        => request('/api/animal-docs/my/count'),
  getMyDoc:      (id)      => request(`/api/animal-docs/${id}/my`),
  markRead:      (id)      => request(`/api/animal-docs/${id}/read`, { method:'PATCH' }),
  userSign:      (id, sig) => request(`/api/animal-docs/${id}/user-sign`, { method:'PATCH', body:JSON.stringify({ userSignature:sig }) }),
}